import { findGraphComponents } from "utils/math/geometry/findGraphComponents";
import { findClosestEdgeInSet } from "utils/math/geometry/findClosestEdgeInSet";
import { findClosestOrthogonalProjection } from "utils/math/geometry/findClosestOrthogonalProjection";
import { getEuclideanDistance } from "utils/math/geometry/getEuclideanDistance";
import { disconnectVertex } from "utils/math/geometry/disconnectVertex";
import { createLShapedConnection } from "utils/math/geometry/createLShapedConnection";
import { addUndirectedEdge } from "utils/math/geometry/addUndirectedEdge";
import { initializeVertex } from "utils/math/geometry/initializeVertex";
import type { NodeMap } from "utils/math/geometry/findClosestPoint/types";
import type { Graph } from "utils/math/geometry/initializeVertex/types";
import { createEdgeSplitQueue, EdgeSplit } from "./edgeSplitQueue";
import { BuildAugmentedGraph, DestinationInfo } from "./types";

export const buildAugmentedGraph: BuildAugmentedGraph = ({
  topology,
  source,
  destinations,
}) => {
  const nodes: NodeMap = {};
  const graph: Graph = {};
  const epsilon = 0.1;

  const { edgeSplits, queueEdgeSplit } = createEdgeSplitQueue({
    nodes,
    graph,
    epsilon,
  });

  // -------------------------------------------------------------------------
  // 1. Initialize Topology
  // -------------------------------------------------------------------------
  for (const [nodeId, nodeData] of Object.entries(topology.nodes)) {
    nodes[nodeId] = nodeData;
    initializeVertex({ graph, nodeId });
  }

  for (const edge of topology.edges) {
    addUndirectedEdge({
      graph,
      nodeA: edge.from,
      nodeB: edge.to,
      weight: edge.weight ?? 1,
    });
  }

  // -------------------------------------------------------------------------
  // 2. Find Components
  // -------------------------------------------------------------------------
  const components = findGraphComponents({ topology });
  const nodeToCompIndex: Record<string, number> = {};

  components.forEach((comp, idx) => {
    for (const nodeId of comp) {
      nodeToCompIndex[nodeId] = idx;
    }
  });

  // -------------------------------------------------------------------------
  // 3. Analyze Source
  // -------------------------------------------------------------------------
  const sourceResult = findClosestOrthogonalProjection({
    point: source,
    topology,
  });

  if (!sourceResult) {
    return { graph, nodes, sourceId: "SOURCE", destinationIds: [] };
  }

  const sourceCompIndex =
    nodeToCompIndex[sourceResult.segment.fromNodeId] ?? -1;

  // -------------------------------------------------------------------------
  // 4. Analyze Destinations & Vote
  // -------------------------------------------------------------------------
  const destInfo: DestinationInfo[] = [];
  const componentVotes = new Map<number, number>();

  for (const dest of destinations) {
    const destResult = findClosestOrthogonalProjection({
      point: dest.coordinates,
      topology,
    });

    if (!destResult) continue;

    const compIdx = nodeToCompIndex[destResult.segment.fromNodeId] ?? -1;

    destInfo.push({
      dest,
      naturalCompIndex: compIdx,
      naturalProjection: destResult,
    });
    componentVotes.set(compIdx, (componentVotes.get(compIdx) ?? 0) + 1);
  }

  // -------------------------------------------------------------------------
  // 5. Determine Winning Component
  // -------------------------------------------------------------------------
  let winningCompIndex: number;

  if (componentVotes.size === 0) {
    winningCompIndex = sourceCompIndex;
  } else if (componentVotes.has(sourceCompIndex)) {
    winningCompIndex = sourceCompIndex;
  } else {
    let bestIdx = -1;
    let bestVotes = -1;
    for (const [idx, votes] of componentVotes.entries()) {
      if (votes > bestVotes) {
        bestVotes = votes;
        bestIdx = idx;
      }
    }
    winningCompIndex = bestIdx;
  }

  const winningComponent = components[winningCompIndex] ?? new Set<string>();

  // -------------------------------------------------------------------------
  // 6. Connect Source
  // -------------------------------------------------------------------------
  const sourceId = "SOURCE";
  const sourceProjId = `${sourceId}_PROJ`;

  const finalSourceProj =
    sourceCompIndex === winningCompIndex
      ? sourceResult
      : findClosestEdgeInSet({
          point: source,
          topology,
          allowedNodeIds: winningComponent,
        }) || sourceResult;

  if (!finalSourceProj) {
    return { graph, nodes, sourceId, destinationIds: [] };
  }

  const resolvedSourceProjId = queueEdgeSplit({
    nodeAId: finalSourceProj.segment.fromNodeId,
    nodeBId: finalSourceProj.segment.toNodeId,
    splitPoint: finalSourceProj.projection.projectedPoint,
    virtualNodeId: sourceProjId,
  });

  createLShapedConnection({
    graph,
    nodes,
    startPoint: source,
    startPointId: sourceId,
    projectionPoint: finalSourceProj.projection.projectedPoint,
    projectionNodeId: resolvedSourceProjId,
  });

  // -------------------------------------------------------------------------
  // 7. Connect Destinations
  // -------------------------------------------------------------------------
  const destinationIds: string[] = [];

  for (let idx = 0; idx < destInfo.length; idx++) {
    const info = destInfo[idx];
    const destId = `DEST_${info.dest.element_id}`;
    const destProjId = `${destId}_PROJ_${idx}`;

    // Decide projection target
    const finalDestProj =
      info.naturalCompIndex === winningCompIndex
        ? info.naturalProjection
        : findClosestEdgeInSet({
            point: info.dest.coordinates,
            topology,
            allowedNodeIds: winningComponent,
          }) || info.naturalProjection;

    if (!finalDestProj) continue;

    const resolvedDestProjId = queueEdgeSplit({
      nodeAId: finalDestProj.segment.fromNodeId,
      nodeBId: finalDestProj.segment.toNodeId,
      splitPoint: finalDestProj.projection.projectedPoint,
      virtualNodeId: destProjId,
    });

    createLShapedConnection({
      graph,
      nodes,
      startPoint: info.dest.coordinates,
      startPointId: destId,
      projectionPoint: finalDestProj.projection.projectedPoint,
      projectionNodeId: resolvedDestProjId,
    });

    destinationIds.push(destId);
  }

  for (const group of edgeSplits.values()) {
    const { nodeAId, nodeBId, splits } = group;
    const nodeA = nodes[nodeAId];
    const nodeB = nodes[nodeBId];

    if (!nodeA || !nodeB) continue;

    disconnectVertex({ graph, fromNode: nodeAId, neighborToRemove: nodeBId });
    disconnectVertex({ graph, fromNode: nodeBId, neighborToRemove: nodeAId });

    const dx = nodeB.x - nodeA.x;
    const dy = nodeB.y - nodeA.y;
    const denom = dx * dx + dy * dy;

    const sortedSplits = splits
      .map((split) => {
        const rawT =
          denom === 0
            ? 0
            : ((split.splitPoint.x - nodeA.x) * dx +
                (split.splitPoint.y - nodeA.y) * dy) /
              denom;
        const t = Math.max(0, Math.min(1, rawT));
        return { ...split, t };
      })
      .sort((a, b) => a.t - b.t);

    const chain: EdgeSplit[] = [];
    let lastPoint = nodeA;

    for (const split of sortedSplits) {
      const distToLast = getEuclideanDistance({
        pointA: split.splitPoint,
        pointB: lastPoint,
      });

      if (distToLast < epsilon) {
        continue;
      }

      chain.push(split);
      lastPoint = split.splitPoint;
    }

    let prevNodeId = nodeAId;
    let prevPoint = nodeA;

    for (const split of chain) {
      const segmentLen = getEuclideanDistance({
        pointA: prevPoint,
        pointB: split.splitPoint,
      });

      addUndirectedEdge({
        graph,
        nodeA: prevNodeId,
        nodeB: split.virtualNodeId,
        weight: segmentLen,
      });

      prevNodeId = split.virtualNodeId;
      prevPoint = split.splitPoint;
    }

    const tailLen = getEuclideanDistance({ pointA: prevPoint, pointB: nodeB });
    addUndirectedEdge({
      graph,
      nodeA: prevNodeId,
      nodeB: nodeBId,
      weight: tailLen,
    });
  }

  return { graph, nodes, sourceId, destinationIds };
};
